"use client";

import { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@zapier/design-system";
import styles from "./TrashMovedPopover.module.css";

const DISMISSED_KEY_PREFIX = "trash-moved-popover-dismissed";

export interface TrashMovedPopoverProps {
  title: string;
  description: string;
  /** Distinguishes dismissal state across surfaces showing different copy (e.g. "zaps", "canvas"). */
  dismissedKey: string;
}

export function TrashMovedPopover({
  title,
  description,
  dismissedKey,
}: TrashMovedPopoverProps) {
  const [isVisible, setIsVisible] = useState(false);
  const storageKey = `${DISMISSED_KEY_PREFIX}-${dismissedKey}`;
  const legacyStorageKey =
    dismissedKey === "zaps" ? DISMISSED_KEY_PREFIX : undefined;

  // localStorage is unavailable during SSR and can throw when storage is
  // disabled, so resolve visibility on the client and treat it as best-effort.
  useEffect(() => {
    if (globalThis.window === undefined) return;
    try {
      const isDismissed =
        localStorage.getItem(storageKey) ||
        (legacyStorageKey && localStorage.getItem(legacyStorageKey));
      setIsVisible(!isDismissed);
    } catch {
      setIsVisible(true);
    }
  }, [legacyStorageKey, storageKey]);

  const handleDismiss = useCallback(() => {
    if (globalThis.window !== undefined) {
      try {
        localStorage.setItem(storageKey, "true");
      } catch {
        // Persistence is best-effort when storage is unavailable.
      }
    }
    setIsVisible(false);
  }, [storageKey]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.trashMovedPopoverWrapper}>
      <Alert
        variant="toast"
        status="info"
        title={title}
        onDismiss={handleDismiss}
      >
        <AlertDescription>{description}</AlertDescription>
      </Alert>
    </div>
  );
}
